import {
    Box,
    Button,
    Chip,
    CircularProgress,
    FormControlLabel,
    Switch,
    TextField,
    Typography,
    useTheme,
} from "@mui/material";
import { useContext, useEffect, useMemo, useState } from "react";
import AuthContext from "../../context/AuthContext";
import useSnackBar from "../../hooks/UseSnackBar";
import { useWaits } from "../../hooks/UseWait";
import { useNavigate, useParams } from "react-router-dom";
import Image1 from "../../images/options/image1.png";
import SnackbarAlert from "../../components/SnackBar";
import Fetch from "../../services/Fetch";
import { buildOptionFormData } from "../../helper/OptionFormData";
import { useConstants } from "../../hooks/UseConstants";
import PageContent from "../../components/PageContent";
import PageHero from "../../components/motion/PageHero";
import DashboardPanelCard from "../../components/cards/DashboardPanelCard";
import ScrollReveal from "../../components/motion/ScrollReveal";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import QuizIcon from "@mui/icons-material/Quiz";

function CreateOption() {
    const { host, language } = useConstants();
    const isAr = language === "ar";
    const theme = useTheme();
    const { wait } = useContext(AuthContext);
    const { openSnackBar, type, message, setSnackBar, setOpenSnackBar } = useSnackBar();
    const { getWait, setGetWait, sendWait, setSendWait } = useWaits();
    const [question, setQuestion] = useState(null);
    const [examName, setExamName] = useState("");
    const [textEn, setTextEn] = useState("");
    const [textAr, setTextAr] = useState("");
    const [isCorrect, setIsCorrect] = useState(false);
    const param = useParams();
    const navigate = useNavigate();

    const optionsPath = `/courses/${param.course_id}/exams/${param.exam_id}/questions/${param.question_id}/options`;

    const questionText = useMemo(() => {
        if (!question) return "";
        return isAr ? question.text_ar : question.text_en;
    }, [question, isAr]);

    const loadQuestion = async () => {
        setGetWait(true);
        const result = await Fetch(
            `${host}/courses/${param.course_id}/exams/${param.exam_id}/show`,
            "GET",
            null
        );
        if (result.status === 200) {
            const exam = result.data.data;
            setExamName(isAr ? exam.name_ar : exam.name_en);
            const found = (exam.questions || []).find(
                (q) => String(q.id) === String(param.question_id)
            );
            setQuestion(found || null);
        }
        setGetWait(false);
    };

    useEffect(() => {
        loadQuestion();
    }, [param.course_id, param.exam_id, param.question_id]);

    const createOption = async () => {
        if (!textEn.trim() || !textAr.trim()) {
            setSnackBar("error", isAr ? "أدخل نص الخيار بالعربية والإنجليزية" : "Enter option text in Arabic and English");
            return;
        }

        setSendWait(true);

        const formData = buildOptionFormData({
            textEn: textEn.trim(),
            textAr: textAr.trim(),
            isCorrect: isCorrect ? 1 : 0,
            questionId: param.question_id,
        });

        const result = await Fetch(`${host}/teacher/courses/exams/questions/options/store`, "POST", formData);

        if (result.status === 200) {
            setSnackBar("success", isAr ? "تم إضافة الخيار وحفظه" : "Option added and saved");
            navigate(optionsPath);
        } else if (result.status === 422) {
            setSnackBar("error", result.data.errors?.[0] || result.data.message);
        } else {
            setSnackBar("error", isAr ? "فشل إضافة الخيار" : "Failed to add option");
        }

        setSendWait(false);
    };

    const fieldSx = { width: "100%" };

    return (
        <>
            <PageContent wait={wait} loading={getWait}>
                <PageHero
                    title={isAr ? "إضافة خيار" : "Add option"}
                    subtitle={isAr ? "أضف خيار إجابة جديد للسؤال" : "Add a new answer option to the question"}
                    icon={<ListAltOutlinedIcon />}
                    actions={
                        <>
                            <Button
                                variant="contained"
                                startIcon={sendWait ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                                onClick={createOption}
                                disabled={sendWait}
                                sx={{ bgcolor: "rgba(255,255,255,0.95)", color: "#0D358C", fontWeight: 700, "&:hover": { bgcolor: "#fff" } }}
                            >
                                {isAr ? "إضافة خيار" : "Add option"}
                            </Button>
                            <Button
                                variant="outlined"
                                startIcon={<ArrowBackIcon />}
                                onClick={() => navigate(optionsPath)}
                                sx={{ color: "white", borderColor: "rgba(255,255,255,0.6)", "&:hover": { borderColor: "white" } }}
                            >
                                {isAr ? "عودة للخيارات" : "Back to options"}
                            </Button>
                        </>
                    }
                />

                {question ? (
                    <ScrollReveal>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                mb: 3,
                                p: 2,
                                borderRadius: 3,
                                bgcolor: theme.palette.background.paper,
                                border: "1px solid",
                                borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0",
                            }}
                        >
                            <Box
                                sx={{
                                    width: 56,
                                    height: 56,
                                    borderRadius: 2,
                                    bgcolor: "#eff6ff",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    flexShrink: 0,
                                }}
                            >
                                <QuizIcon sx={{ color: "#0D358C", fontSize: 32 }} />
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                    {isAr ? "الاختبار" : "Exam"}: {examName}
                                </Typography>
                                <Typography variant="h6" fontWeight={800} sx={{ color: "#0D358C" }} noWrap>
                                    {questionText}
                                </Typography>
                                <Chip size="small" label={`ID #${question.id}`} sx={{ mt: 0.5 }} />
                            </Box>
                        </Box>
                    </ScrollReveal>
                ) : null}

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", lg: "row" },
                        direction: "ltr",
                        gap: 3,
                        alignItems: "stretch",
                    }}
                >
                    <ScrollReveal delay={50} className="w-full lg:w-[38%]">
                        <Box
                            sx={{
                                height: "100%",
                                minHeight: { lg: 380 },
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                justifyContent: "center",
                                gap: 2,
                                p: 4,
                                borderRadius: 4,
                                border: "1px solid",
                                borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0",
                                background: theme.palette.mode === "dark"
                                    ? "linear-gradient(160deg, #1a2744 0%, #121212 100%)"
                                    : "linear-gradient(160deg, #e8f0fa 0%, #f5f8fc 100%)",
                            }}
                        >
                            <Box
                                component="img"
                                src={Image1}
                                alt=""
                                sx={{ width: "100%", maxWidth: 280, height: "auto", objectFit: "contain" }}
                            />
                            <Typography variant="body2" sx={{ color: "text.secondary", textAlign: "center", maxWidth: 280, lineHeight: 1.7 }}>
                                {isAr
                                    ? "حدّد إذا كان هذا الخيار هو الإجابة الصحيحة للسؤال"
                                    : "Mark whether this option is the correct answer"}
                            </Typography>
                        </Box>
                    </ScrollReveal>

                    <Box sx={{ flex: 1, minWidth: 0, direction: isAr ? "rtl" : "ltr" }}>
                        <DashboardPanelCard
                            title={isAr ? "بيانات الخيار" : "Option details"}
                            subtitle={isAr ? "يُحفظ في قاعدة البيانات ويرتبط بالسؤال" : "Saved to the database and linked to the question"}
                        >
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                                <TextField
                                    sx={fieldSx}
                                    value={textEn}
                                    onChange={(e) => setTextEn(e.target.value)}
                                    label={isAr ? "نص الخيار بالإنجليزية" : "Option text (English)"}
                                    variant="outlined"
                                    size="small"
                                    multiline
                                    minRows={2}
                                    required
                                />
                                <TextField
                                    sx={fieldSx}
                                    value={textAr}
                                    onChange={(e) => setTextAr(e.target.value)}
                                    label={isAr ? "نص الخيار بالعربية" : "Option text (Arabic)"}
                                    variant="outlined"
                                    size="small"
                                    multiline
                                    minRows={2}
                                    required
                                />

                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "space-between",
                                        flexWrap: "wrap",
                                        gap: 2,
                                        p: 2,
                                        borderRadius: 2,
                                        border: "2px solid",
                                        borderColor: isCorrect ? "#86efac" : (theme.palette.mode === "dark" ? "divider" : "#e2e8f0"),
                                        bgcolor: isCorrect
                                            ? (theme.palette.mode === "dark" ? "rgba(22,163,74,0.12)" : "#f0fdf4")
                                            : (theme.palette.mode === "dark" ? "background.default" : "#f8fafc"),
                                    }}
                                >
                                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                        <CheckCircleOutlineIcon sx={{ color: isCorrect ? "#16a34a" : "#94a3b8" }} />
                                        <Box>
                                            <Typography variant="subtitle2" fontWeight={700}>
                                                {isAr ? "إجابة صحيحة؟" : "Correct answer?"}
                                            </Typography>
                                            <Typography variant="caption" sx={{ color: "text.secondary" }}>
                                                {isAr ? "فعّل إذا كان هذا الخيار صحيحاً" : "Enable if this is the correct option"}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={isCorrect}
                                                onChange={(e) => setIsCorrect(e.target.checked)}
                                                color="success"
                                            />
                                        }
                                        label={isCorrect ? (isAr ? "نعم" : "Yes") : (isAr ? "لا" : "No")}
                                    />
                                </Box>

                                <Box
                                    sx={{
                                        display: "flex",
                                        flexWrap: "wrap",
                                        gap: 1.5,
                                        justifyContent: "flex-end",
                                        mt: 1,
                                        pt: 2,
                                        borderTop: "1px solid",
                                        borderColor: theme.palette.mode === "dark" ? "divider" : "#e2e8f0",
                                    }}
                                >
                                    <Button variant="outlined" onClick={() => navigate(optionsPath)} disabled={sendWait}>
                                        {isAr ? "إلغاء" : "Cancel"}
                                    </Button>
                                    <Button
                                        variant="contained"
                                        size="large"
                                        onClick={createOption}
                                        disabled={sendWait}
                                        startIcon={sendWait ? <CircularProgress size={18} color="inherit" /> : <AddIcon />}
                                        sx={{
                                            minWidth: 200,
                                            px: 3,
                                            bgcolor: "#0D358C",
                                            fontWeight: 700,
                                            boxShadow: "0 4px 14px rgba(13,53,140,0.35)",
                                            "&:hover": { bgcolor: "#1d4ed8" },
                                        }}
                                    >
                                        {sendWait ? (isAr ? "جاري الحفظ..." : "Saving...") : (isAr ? "إضافة خيار" : "Add option")}
                                    </Button>
                                </Box>
                            </Box>
                        </DashboardPanelCard>
                    </Box>
                </Box>
            </PageContent>
            <SnackbarAlert open={openSnackBar} message={message} severity={type} onClose={() => setOpenSnackBar(false)} />
        </>
    );
}

export default CreateOption;
